// src/services/api.js - FIXED VERSION
import axios from 'axios';

// Create the API instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5076/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('auth_token');
    
    console.log('API Request:', {
      url: config.url,
      method: config.method,
      baseURL: config.baseURL,
      fullURL: config.baseURL + config.url,
      hasToken: !!token,
      token: token ? `${token.substring(0, 20)}...` : 'none'
    });
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('No auth token found in localStorage');
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', {
      url: response.config.url,
      status: response.status,
      ok: response.status >= 200 && response.status < 300
    });
    return response;
  },
  (error) => {
    console.error('API Error:', {
      url: error.config?.url,
      status: error.response?.status,
      error: error.response?.data || error.message
    });

    if (error.response?.status === 401) {
      console.warn('401 Unauthorized - clearing auth data and redirecting to login');
      
      // Clear auth data
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_data');
      
      // Redirect to login if not already there
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper function to check if user is authenticated
export const isAuthenticated = () => {
  const token = localStorage.getItem('auth_token');
  if (!token) return false;
  
  try {
    // Check if token is expired
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const decoded = JSON.parse(jsonPayload);
    const currentTime = Date.now() / 1000;
    
    if (decoded.exp < currentTime) {
      console.warn('Token is expired');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_data');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error validating token:', error);
    return false;
  }
};

// Enhanced API methods with better error handling
const apiService = {
  async get(url, config = {}) {
    try {
      console.log('GET request to:', url);
      const response = await api.get(url, config);
      return response;
    } catch (error) {
      console.error('GET error:', error);
      throw new Error(`GET ${url} failed: ${error.response?.status} ${error.response?.statusText || error.message}`);
    }
  },

  async post(url, data, config = {}) {
    try {
      console.log('POST request to:', url);
      const response = await api.post(url, data, config);
      return response;
    } catch (error) {
      console.error('POST error:', error);
      throw new Error(`POST ${url} failed: ${error.response?.status} ${error.response?.statusText || error.message}`);
    }
  },

  async put(url, data, config = {}) {
    try {
      console.log('PUT request to:', url);
      const response = await api.put(url, data, config);
      return response;
    } catch (error) {
      console.error('PUT error:', error);
      throw new Error(`PUT ${url} failed: ${error.response?.status} ${error.response?.statusText || error.message}`);
    }
  },

  async delete(url, config = {}) {
    try {
      console.log('DELETE request to:', url);
      const response = await api.delete(url, config);
      return response;
    } catch (error) {
      console.error('DELETE error:', error);
      throw new Error(`DELETE ${url} failed: ${error.response?.status} ${error.response?.statusText || error.message}`);
    }
  },

  // Helper to get auth headers manually
  getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  },

  // Test auth endpoint
  async testAuth() {
    try {
      const response = await this.get('/auth/me');
      console.log('Auth test successful:', response.data);
      return response.data;
    } catch (error) {
      console.error('Auth test failed:', error);
      throw error;
    }
  }
};

export default apiService;
export { api };